import { Locale } from 'base/ui/locales';
import { computed, makeObservable } from 'mobx';
import {
  aSpace,
  PreferredGenPageNumbers,
  Project,
  Space,
  SpaceState,
} from '../model';
import { removeAllThreadsFromPage } from '../model_comment_helpers';
import { DEFAULT_HOME_SPACE_NAME } from '../model_constants';
import { SelectionStore } from '../selection/store';
import { EditingActionConfirmations } from './confirmations';
import { clearHomepage } from './page';

export class HomepageSpaceEditingActions {
  constructor(
    private readonly selection: SelectionStore,
    private readonly confirmations: EditingActionConfirmations,
  ) {
    makeObservable(this);
  }

  readonly setName = (name: string) => {
    this.selection.project.homeSpaceName = name;
  };

  readonly setDescription = (description: string) => {
    this.selection.project.brief = description;
  };

  readonly setLocale = (locale: Locale) => {
    this.selection.project.locale = locale;
  };

  readonly duplicate = () => {
    const {
      spaces,
      homePage,
      homeSpaceName = DEFAULT_HOME_SPACE_NAME,
    } = this.selection.project;

    const duplicateState = aSpace({
      name: `Copy of ${homeSpaceName}`,
      subPages: [
        // Comment threads should remain linked to the original page.
        removeAllThreadsFromPage(homePage.state),
      ],
      preferredNumPages: this.selection.project.preferredNumPages,
    });
    spaces.splice(0, 0, duplicateState);
  };

  @computed
  private get delete() {
    const { project } = this.selection;
    const { spaces } = project;
    if (spaces.length > 0) {
      // Replace home with the .spaces[0]
      return () => {
        const { homePageSpace } = moveSpaceToTop(this.selection, spaces[0]);
        spaces.delete(homePageSpace);
      };
    } else {
      // Delete by resetting to default
      return () => {
        project.homeSpaceName = undefined;
        clearHomepage(project);
      };
    }
  }

  readonly deleteWithConfirm = async () => {
    const { project } = this.selection;
    const ok = await this.confirmations.confirmDeleteSpace(
      project.homeSpaceName || DEFAULT_HOME_SPACE_NAME,
    );
    if (!ok) return;
    this.delete();
  };

  readonly moveToTop = undefined;
  readonly moveUp = undefined;

  @computed
  get moveDown() {
    const { spaces } = this.selection.project;
    if (spaces.length === 0) return;
    return () => {
      const { homePageSpace } = moveSpaceToTop(this.selection, spaces[0]);
      this.selection.set({ space: homePageSpace });
    };
  }

  readonly setPreferredNumOfPages = (numPages: PreferredGenPageNumbers) => {
    this.selection.project.preferredNumPages = numPages;
  };
}

export class SpaceEditingActions {
  constructor(
    private readonly selection: SelectionStore,
    private readonly self: Space,
    private readonly confirmations: EditingActionConfirmations,
  ) {
    makeObservable(this);
  }

  readonly setName = (name: string) => {
    this.self.name = name;
  };

  readonly setDescription = (description: string) => {
    this.self.description = description;
  };

  readonly setLocale = (locale: Locale) => {
    this.self.localeOverride =
      locale === this.selection.project.locale ? undefined : locale;
  };

  readonly duplicate = () => {
    const { spaces } = this.selection.project;
    const index = spaces.indexOf(this.self);
    spaces.splice(index + 1, 0, {
      ...this.self.state,
      name: 'Copy of ' + this.self.name,
      preferredNumPages: this.self.preferredNumPages,
    });
  };

  readonly deleteWithConfirm = async () => {
    const { spaces } = this.selection.project;
    const ok = await this.confirmations.confirmDeleteSpace(this.self.name);
    if (!ok) return;
    spaces.delete(this.self);
  };

  readonly moveToTop = () => {
    moveSpaceToTop(this.selection, this.self);
  };

  readonly moveUp = () => {
    const { spaces } = this.selection.project;
    const index = spaces.indexOf(this.self);
    if (index === 0) {
      this.moveToTop();
    } else {
      spaces.splice(index, 1);
      spaces.splice(index - 1, 0, this.self.state);
      const newSpace = spaces[index - 1];
      this.selection.set({ space: newSpace });
    }
  };

  @computed
  get moveDown() {
    const { spaces } = this.selection.project;
    const index = spaces.indexOf(this.self);
    if (index === spaces.length - 1) return;
    return () => {
      spaces.splice(index, 1);
      spaces.splice(index + 1, 0, this.self.state);
      const newSpace = spaces[index + 1];
      this.selection.set({ space: newSpace });
    };
  }

  readonly setPreferredNumOfPages = (numPages: PreferredGenPageNumbers) => {
    this.self.preferredNumPages = numPages;
  };
}

function moveSpaceToTop(
  selection: SelectionStore,
  space: Space,
): { homePageSpace: Space } {
  const { project } = selection;
  // Move homePage down:
  project.spaces.splice(
    0,
    0,
    aSpace({
      name: filterAutogeneratedNames(project.homeSpaceName) || 'Sitemap 1',
      description: project.brief,
      subPages: [project.homePage.state],
    }),
  );
  const homePageSpace = project.spaces[0];
  // Move space into homePage:
  const index = project.spaces.indexOf(space);
  project.spaces.splice(index, 1);
  putSpaceIntoHome(selection.project, space.state);
  selection.set({ space: 'home' });
  return { homePageSpace };
}

function putSpaceIntoHome(project: Project, space: SpaceState) {
  const { homePage } = project;
  const [firstPage, ...otherPages] = space.subPages;
  homePage.name = firstPage.name;
  homePage.sections.clear();
  homePage.sections.push(...firstPage.sections);
  homePage.subPages.clear();
  homePage.subPages.push(...firstPage.subPages, ...otherPages);
  project.homeSpaceName = filterAutogeneratedNames(space.name);
  project.brief = space.description || project.brief;
}

function filterAutogeneratedNames(name: string | undefined) {
  if (!name) return;
  const norm = name.toLowerCase().trim();
  return norm.match(/^(space|sitemap)\s*\d+$/) ||
    norm === DEFAULT_HOME_SPACE_NAME.toLowerCase()
    ? undefined
    : name;
}

export const dropSpaceAt =
  (selection: SelectionStore) =>
  (source: { space: SpaceState; ref: Space | 'home' }, destination: number) => {
    const { project } = selection;
    // add the source at destination
    let newSpace;
    if (destination === 0) {
      project.spaces.splice(
        0,
        0,
        aSpace({
          name: filterAutogeneratedNames(project.homeSpaceName) || 'Sitemap 1',
          description: project.brief,
          subPages: [project.homePage.state],
        }),
      );
      putSpaceIntoHome(project, source.space);
      newSpace = 'home' as const;
    } else {
      const index = destination - 1;
      project.spaces.splice(index, 0, source.space);
      newSpace = project.spaces[index];
    }
    // it must be visible enough, because we're dropping there
    selection.set({ space: newSpace }, { disableEnsureVisible: true });

    // delete the source from its original location
    if (source.ref === 'home') {
      const [firstSpace] = project.spaces.splice(0, 1);
      putSpaceIntoHome(project, firstSpace);
    } else {
      project.spaces.delete(source.ref);
    }
  };
